import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import type { StatusUrzadzenia } from '../api';

/**
 * Elementy powtarzalne interfejsu. Trzymają się kontraktu z CLAUDE.md §5:
 * promień zero, brak cieni, kolor marki wyłącznie na akcji głównej i na oznaczeniu
 * wyboru, kolor statusu wyłącznie jako chip albo lewa krawędź wiersza.
 */

const STATUSY: Record<StatusUrzadzenia, { nazwa: string; token: string }> = {
  SPRAWNE: { nazwa: 'Sprawne', token: 'done' },
  W_NAPRAWIE: { nazwa: 'W naprawie', token: 'progress' },
  ZASTEPCZE_U_KLIENTA: { nazwa: 'Zastępcze u klienta', token: 'hold' },
  // Wycofana maszyna jest faktem administracyjnym, nie stanem wymagającym uwagi —
  // dlatego szarość, a nie kolor statusu.
  WYCOFANE: { nazwa: 'Wycofane', token: '' },
};

export const KOLEJNOSC_STATUSOW: StatusUrzadzenia[] = [
  'SPRAWNE',
  'W_NAPRAWIE',
  'ZASTEPCZE_U_KLIENTA',
  'WYCOFANE',
];

export function nazwaStatusu(status: StatusUrzadzenia): string {
  return STATUSY[status].nazwa;
}

export function ChipStatusu({ status }: { status: StatusUrzadzenia }) {
  const { nazwa, token } = STATUSY[status];

  const styl = token
    ? { background: `var(--status-${token}-bg)`, color: `var(--status-${token}-text)` }
    : { background: 'var(--n-100)', color: 'var(--n-700)' };

  return (
    <span className="t-label inline-block px-9 py-4 whitespace-nowrap" style={styl}>
      {nazwa}
    </span>
  );
}

const STATUSY_ZLECENIA: Record<string, { nazwa: string; token: string }> = {
  NOWE: { nazwa: 'Nowe', token: 'new' },
  W_TOKU: { nazwa: 'W toku', token: 'progress' },
  WSTRZYMANE: { nazwa: 'Wstrzymane', token: 'hold' },
  ZAKONCZONE: { nazwa: 'Zakończone', token: 'done' },
  // Rozliczone i odrzucone to sprawy zamknięte — szarość, nie kolor statusu.
  ROZLICZONE: { nazwa: 'Rozliczone', token: '' },
  ODRZUCONE: { nazwa: 'Odrzucone', token: '' },
};

export function nazwaStatusuZlecenia(status: string): string {
  return STATUSY_ZLECENIA[status]?.nazwa ?? status;
}

export function ChipZlecenia({ status }: { status: string }) {
  const opis = STATUSY_ZLECENIA[status] ?? { nazwa: status, token: '' };
  const styl = opis.token
    ? { background: `var(--status-${opis.token}-bg)`, color: `var(--status-${opis.token}-text)` }
    : { background: 'var(--n-100)', color: 'var(--n-700)' };

  return (
    <span className="t-label inline-block px-9 py-4 whitespace-nowrap" style={styl}>
      {opis.nazwa}
    </span>
  );
}

const STATUSY_MONTAZU: Record<string, { nazwa: string; token: string }> = {
  NOWE: { nazwa: 'Nowe', token: 'new' },
  W_TOKU: { nazwa: 'W toku', token: 'progress' },
  WYKONANE: { nazwa: 'Wykonane', token: 'done' },
};

export function nazwaStatusuMontazu(status: string): string {
  return STATUSY_MONTAZU[status]?.nazwa ?? status;
}

export function ChipMontazu({ status }: { status: string }) {
  const opis = STATUSY_MONTAZU[status] ?? { nazwa: status, token: '' };

  const styl = opis.token
    ? { background: `var(--status-${opis.token}-bg)`, color: `var(--status-${opis.token}-text)` }
    : { background: 'var(--n-100)', color: 'var(--n-700)' };

  return (
    <span className="t-label inline-block px-9 py-4 whitespace-nowrap" style={styl}>
      {opis.nazwa}
    </span>
  );
}

/** Neutralna etykietka na dane pomocnicze (tryb, kanał, kategoria). */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="t-small border border-n-100 bg-n-50 px-7 py-4 whitespace-nowrap text-n-700">
      {children}
    </span>
  );
}

export function Karta({
  tytul,
  akcje,
  wypelnienie = true,
  wyroznienie = false,
  children,
}: {
  tytul?: string;
  akcje?: ReactNode;
  /** `false`, gdy wnętrzem karty jest tabela — ta ma własne odstępy. */
  wypelnienie?: boolean;
  /**
   * Blok, na który trzeba spojrzeć najpierw (pola wymagane przy zakładaniu karty).
   * Wyróżniony ciemniejszą krawędzią i tłem, nie kolorem marki — marka należy się
   * akcji i wyborowi, a nie „to jest ważne" (CLAUDE.md §5).
   */
  wyroznienie?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={`mb-14 border last:mb-0 ${
        wyroznienie ? 'border-n-300 bg-n-50' : 'border-n-100 bg-surface'
      }`}
    >
      {tytul && (
        <header
          className={`flex min-h-44 flex-wrap items-center gap-10 border-b px-14 py-7 ${
            wyroznienie ? 'border-b-n-300' : 'border-b-n-100'
          }`}
        >
          <h2 className={`t-mono-micro mr-auto ${wyroznienie ? 'text-n-800' : 'text-n-500'}`}>
            {tytul}
          </h2>
          {akcje}
        </header>
      )}
      <div className={wypelnienie ? 'p-14' : ''}>{children}</div>
    </section>
  );
}

/** Para etykieta–wartość w szerokiej siatce. Wartość pusta pokazuje myślnik. */
export function Dana({
  etykieta,
  children,
  mono = false,
}: {
  etykieta: string;
  children?: ReactNode;
  mono?: boolean;
}) {
  const puste = children === null || children === undefined || children === '';

  return (
    <>
      <dt className="t-label text-n-600">{etykieta}</dt>
      <dd
        className={`m-0 ${mono ? 't-mono-data' : 't-body'} ${puste ? 'text-n-300' : 'text-n-900'}`}
      >
        {puste ? '—' : children}
      </dd>
    </>
  );
}

export function Komunikat({ tresc, rodzaj = 'blad' }: { tresc: string; rodzaj?: 'blad' | 'info' }) {
  const styl =
    rodzaj === 'blad'
      ? {
          borderLeftColor: 'var(--status-failure)',
          background: 'var(--status-failure-bg)',
          color: 'var(--status-failure-text)',
        }
      : { borderLeftColor: 'var(--n-300)', background: 'var(--n-50)', color: 'var(--n-700)' };

  return (
    <p
      role={rodzaj === 'blad' ? 'alert' : undefined}
      className="t-small border-l-[3px] px-13 py-10"
      style={styl}
    >
      {tresc}
    </p>
  );
}

export function Pusto({ tresc }: { tresc: string }) {
  return <p className="t-small px-14 py-34 text-center text-n-500">{tresc}</p>;
}

/** Akcja główna widoku — jedyny przycisk w kolorze marki (CLAUDE.md §5). */
export function PrzyciskMarki({
  children,
  ikona,
  ...reszta
}: ButtonHTMLAttributes<HTMLButtonElement> & { ikona?: ReactNode }) {
  return (
    <button
      {...reszta}
      className="flex items-center gap-7 border border-brand-600 bg-brand-500 px-14 text-surface disabled:border-n-300 disabled:bg-n-300"
    >
      {ikona}
      <span className="t-body-strong">{children}</span>
    </button>
  );
}

export function Przycisk({
  children,
  ikona,
  ...reszta
}: ButtonHTMLAttributes<HTMLButtonElement> & { ikona?: ReactNode }) {
  return (
    <button
      {...reszta}
      className="flex items-center gap-7 border border-n-300 bg-surface px-13 text-n-700 hover:bg-n-50 disabled:text-n-300"
    >
      {ikona}
      <span className="t-label">{children}</span>
    </button>
  );
}

/**
 * Chip filtra. Włączony nosi kolor marki — to oznaczenie wyboru, nie akcja:
 * mówi „patrzysz na to", a nie „kliknij tu" (CLAUDE.md §5).
 */
export function ChipFiltra({
  aktywny,
  licznik,
  children,
  ...reszta
}: ButtonHTMLAttributes<HTMLButtonElement> & { aktywny: boolean; licznik?: number }) {
  return (
    <button
      {...reszta}
      aria-pressed={aktywny}
      className={`flex items-center gap-5 border px-13 ${
        aktywny
          ? 'border-brand-500 bg-brand-50 text-brand-700'
          : 'border-n-300 bg-surface text-n-700 hover:bg-n-50'
      }`}
    >
      <span className="t-label">{children}</span>
      {licznik !== undefined && (
        <span className={`t-mono-data ${aktywny ? 'text-brand-700' : 'text-n-500'}`}>
          {licznik}
        </span>
      )}
    </button>
  );
}

export function PoleTekstowe({
  etykieta,
  podpowiedz,
  ...reszta
}: { etykieta: string; podpowiedz?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="t-label mb-5 block text-n-700">
        {etykieta}
        {podpowiedz && <span className="t-small text-n-500"> — {podpowiedz}</span>}
      </span>
      <input
        {...reszta}
        className="w-full border border-n-300 bg-surface px-13 text-n-900 disabled:bg-n-50 disabled:text-n-500"
      />
    </label>
  );
}

export function PoleWyboru({
  etykieta,
  podpowiedz,
  children,
  ...reszta
}: { etykieta: string; podpowiedz?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <label className="block">
      <span className="t-label mb-5 block text-n-700">
        {etykieta}
        {podpowiedz && <span className="t-small text-n-500"> — {podpowiedz}</span>}
      </span>
      <select
        {...reszta}
        className="w-full border border-n-300 bg-surface px-13 text-n-900 disabled:bg-n-50"
      >
        {children}
      </select>
    </label>
  );
}
